import React, { useState } from 'react';
import { Card, Form, Button, Row, Col } from 'react-bootstrap';

const FooterEditor = ({ footer, onChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!footer) return null;

  const handleLeftSectionChange = (field, value) => {
    onChange({
      ...footer,
      leftSection: {
        ...footer.leftSection,
        [field]: value
      }
    });
  };

  const handleSocialLinkChange = (platform, value) => {
    onChange({
      ...footer,
      leftSection: {
        ...footer.leftSection,
        socialLinks: {
          ...footer.leftSection.socialLinks,
          [platform]: value
        }
      }
    });
  };

  const handleMiddleSectionChange = (field, value) => {
    onChange({
      ...footer,
      middleSection: {
        ...footer.middleSection,
        [field]: value
      }
    });
  };

  const handleMenuChange = (index, field, value) => {
    const newMenus = [...footer.middleSection.menus];
    newMenus[index] = {
      ...newMenus[index],
      [field]: value
    };
    onChange({
      ...footer,
      middleSection: {
        ...footer.middleSection,
        menus: newMenus
      }
    });
  };

  const handleAddMenu = () => {
    onChange({
      ...footer,
      middleSection: {
        ...footer.middleSection,
        menus: [
          ...footer.middleSection.menus,
          { label: '', link: '' }
        ]
      }
    });
  };

  const handleRemoveMenu = (index) => {
    const newMenus = footer.middleSection.menus.filter((_, i) => i !== index);
    onChange({
      ...footer,
      middleSection: {
        ...footer.middleSection,
        menus: newMenus
      }
    });
  };

  const handleRightSectionChange = (field, value) => {
    onChange({
      ...footer,
      rightSection: {
        ...footer.rightSection,
        [field]: value
      }
    });
  };

  const handleContactMenuChange = (index, field, value) => {
    const newMenus = [...footer.rightSection.menus];
    newMenus[index] = {
      ...newMenus[index],
      [field]: value
    };
    onChange({
      ...footer,
      rightSection: {
        ...footer.rightSection,
        menus: newMenus
      }
    });
  };

  const handleAddContactMenu = () => {
    onChange({
      ...footer,
      rightSection: {
        ...footer.rightSection,
        menus: [
          ...footer.rightSection.menus,
          {}
        ]
      }
    });
  };

  const handleRemoveContactMenu = (index) => {
    const newMenus = footer.rightSection.menus.filter((_, i) => i !== index);
    onChange({
      ...footer,
      rightSection: {
        ...footer.rightSection,
        menus: newMenus
      }
    });
  };

  return (
    <Card className="mb-4">
      <Card.Header 
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: 'pointer' }}
        className="d-flex justify-content-between align-items-center"
      >
        <h5 className="m-0">Footer Section</h5>
        <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
      </Card.Header>
      
      {isExpanded && (
        <Card.Body>
          <Row>
            {/* Left Section */}
            <Col md={4}>
              <h6 className="mb-3">Left Section</h6>
              
              <Form.Group className="mb-3">
                <Form.Label>Logo</Form.Label>
                <Form.Control
                  type="text"
                  value={footer.leftSection.logo || ''}
                  onChange={(e) => handleLeftSectionChange('logo', e.target.value)}
                  placeholder="Logo identifier"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description Text</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={footer.leftSection.text || ''}
                  onChange={(e) => handleLeftSectionChange('text', e.target.value)}
                  placeholder="Footer description"
                />
              </Form.Group>

              <h6 className="mb-2">Social Links</h6>
              {footer.leftSection.socialLinks && Object.entries(footer.leftSection.socialLinks).map(([platform, url]) => (
                <Form.Group key={platform} className="mb-2">
                  <Form.Label className="text-capitalize">{platform}</Form.Label>
                  <Form.Control
                    type="url"
                    value={url || ''}
                    onChange={(e) => handleSocialLinkChange(platform, e.target.value)}
                    placeholder={`${platform} URL`}
                  />
                </Form.Group>
              ))}
            </Col>

            {/* Middle Section */}
            <Col md={4}>
              <h6 className="mb-3">Middle Section (Services)</h6>
              
              <Form.Group className="mb-3">
                <Form.Label>Heading</Form.Label>
                <Form.Control
                  type="text"
                  value={footer.middleSection.heading || ''}
                  onChange={(e) => handleMiddleSectionChange('heading', e.target.value)}
                  placeholder="Section heading"
                />
              </Form.Group>

              <h6 className="mb-2">Service Links</h6>
              {footer.middleSection.menus.map((menu, index) => (
                <div key={index} className="border p-2 mb-2 rounded">
                  <Form.Group className="mb-2">
                    <Form.Label>Label</Form.Label>
                    <Form.Control
                      type="text"
                      value={menu.label || ''}
                      onChange={(e) => handleMenuChange(index, 'label', e.target.value)}
                      placeholder="Service name"
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Link</Form.Label>
                    <Form.Control
                      type="url"
                      value={menu.link || ''}
                      onChange={(e) => handleMenuChange(index, 'link', e.target.value)}
                      placeholder="Service URL"
                    />
                  </Form.Group>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemoveMenu(index)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleAddMenu}
                className="w-100"
              >
                + Add Service Link
              </Button>
            </Col>

            {/* Right Section */}
            <Col md={4}>
              <h6 className="mb-3">Right Section (Contact)</h6>
              
              <Form.Group className="mb-3">
                <Form.Label>Heading</Form.Label>
                <Form.Control
                  type="text"
                  value={footer.rightSection.heading || ''}
                  onChange={(e) => handleRightSectionChange('heading', e.target.value)}
                  placeholder="Section heading"
                />
              </Form.Group>

              <h6 className="mb-2">Contact Information</h6>
              {footer.rightSection.menus.map((menu, index) => (
                <div key={index} className="border p-2 mb-2 rounded">
                  {Object.keys(menu).map((key) => (
                    <Form.Group key={key} className="mb-2">
                      <Form.Label className="text-capitalize">{key}</Form.Label>
                      <Form.Control
                        type="text"
                        value={menu[key] || ''}
                        onChange={(e) => handleContactMenuChange(index, key, e.target.value)}
                        placeholder={`Enter ${key}`}
                      />
                    </Form.Group>
                  ))}
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemoveContactMenu(index)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleAddContactMenu}
                className="w-100"
              >
                + Add Contact Item
              </Button>
            </Col>
          </Row>

          <Form.Group className="mt-3">
            <Form.Label>Copyright Text</Form.Label>
            <Form.Control
              type="text"
              value={footer.copyright || ''}
              onChange={(e) => onChange({ ...footer, copyright: e.target.value })}
              placeholder="Copyright text"
            />
          </Form.Group>
        </Card.Body>
      )}
    </Card>
  );
};

export default FooterEditor;
